import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Upload,
  CheckCircle2,
  XCircle,
  Key,
  Unlock,
  Archive,
  Copy,
  FileText,
  AlertCircle,
  CheckCheck,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

import PageHeader from '../../components/layout/PageHeader';
import { notificationService } from '../../services/notification.service';
import type { Notification } from '../../types/notification.types';
import { useNotificationStore } from '../../store/notificationStore';
import { ROUTES } from '../../config/routes.config';
import { useAuthStore } from '../../store/authStore';
import { formatDocumentDisplayName } from '../../utils/documentName';

const NOTIFICATION_ICONS: Record<
  string,
  { icon: React.ReactNode; color: string; bg: string }
> = {
  document_uploaded: {
    icon: <Upload size={16} />,
    color: 'var(--color-primary)',
    bg: 'var(--color-primary-50)',
  },
  document_upload_pending: {
    icon: <Upload size={16} />,
    color: 'var(--color-primary)',
    bg: 'var(--color-primary-50)',
  },
  document_approved: {
    icon: <CheckCircle2 size={16} />,
    color: 'var(--color-success)',
    bg: 'var(--color-success-light)',
  },
  document_denied: {
    icon: <XCircle size={16} />,
    color: 'var(--color-error)',
    bg: 'var(--color-error-light)',
  },
  document_rejected: {
    icon: <XCircle size={16} />,
    color: 'var(--color-error)',
    bg: 'var(--color-error-light)',
  },
  access_request_submitted: {
    icon: <Key size={16} />,
    color: 'var(--color-warning)',
    bg: 'var(--color-warning-light)',
  },
  access_request_pending: {
    icon: <Key size={16} />,
    color: 'var(--color-warning)',
    bg: 'var(--color-warning-light)',
  },
  access_request_approved: {
    icon: <Unlock size={16} />,
    color: 'var(--color-success)',
    bg: 'var(--color-success-light)',
  },
  access_approved: {
    icon: <Unlock size={16} />,
    color: 'var(--color-success)',
    bg: 'var(--color-success-light)',
  },
  access_request_denied: {
    icon: <XCircle size={16} />,
    color: 'var(--color-error)',
    bg: 'var(--color-error-light)',
  },
  access_rejected: {
    icon: <XCircle size={16} />,
    color: 'var(--color-error)',
    bg: 'var(--color-error-light)',
  },
  document_archived: {
    icon: <Archive size={16} />,
    color: 'var(--color-gray-600)',
    bg: 'var(--color-gray-100)',
  },
  document_moved: {
    icon: <FileText size={16} />,
    color: 'var(--color-primary)',
    bg: 'var(--color-primary-50)',
  },
  document_copied: {
    icon: <Copy size={16} />,
    color: 'var(--color-primary)',
    bg: 'var(--color-primary-50)',
  },
  general: {
    icon: <Bell size={16} />,
    color: 'var(--color-gray-600)',
    bg: 'var(--color-gray-100)',
  },
};
const DEFAULT_ICON = {
  icon: <Bell size={16} />,
  color: 'var(--color-gray-500)',
  bg: 'var(--color-gray-100)',
};

type FilterTab = 'All' | 'Unread' | 'Read';

const TAB_IS_READ: Record<FilterTab, boolean | undefined> = {
  All: undefined,
  Unread: false,
  Read: true,
};

function mapRowToNotification(row: any): Notification {
  return {
    notification_id: row.notification_id,
    user_id: row.for_user_id ?? 0,
    type: row.type ?? 'general',
    message: row.message ?? row.title ?? '',
    reference_type: row.reference_type ?? null,
    reference_id: row.reference_id ?? null,
    is_read: !!row.read_at,
    created_at: row.created_at,
    original_file_name: row.original_file_name,
    cms_number: row.cms_number,
    ps_name: row.ps_name,
    case_type: row.case_type,
    metadata: row.metadata,
  };
}

const NotificationItem: React.FC<{
  notification: Notification;
  onRead: (id: string) => void;
}> = ({ notification, onRead }) => {
  const navigate = useNavigate();
  const { can } = useAuthStore();
  const iconConfig =
    NOTIFICATION_ICONS[notification.type] ?? DEFAULT_ICON;

  const fileName = notification.original_file_name
    ? formatDocumentDisplayName({
        cmsNumber: notification.cms_number,
        originalFileName: notification.original_file_name,
        documentType: (notification.metadata as any)?.document_type,
      })
    : notification.metadata?.file_name ?? null;

  const isManagerView = can('records.approve', [
    'RRMS_CONTENT_MANAGER',
    'RRMS_SUPER_ADMIN',
  ]);

  const getTargetRoute = () => {
    if (notification.reference_type === 'case' && notification.reference_id) {
      return `/cases/${notification.reference_id}`;
    }

    if (
      notification.type === 'document_upload_pending' &&
      isManagerView
    ) {
      return `${ROUTES.CONTENT_MGMT}?tab=approvals&subtab=pending`;
    }

    if (
      notification.reference_type === 'access_request' ||
      notification.type.startsWith('access_')
    ) {
      if (isManagerView && notification.type === 'access_request_pending') {
        return `${ROUTES.CONTENT_MGMT}?tab=approvals&subtab=access`;
      }

      if (
        notification.type === 'access_approved' ||
        notification.type === 'access_request_approved'
      ) {
        return `${ROUTES.ACCESS_STATUS}?tab=approved`;
      }

      if (
        notification.type === 'access_rejected' ||
        notification.type === 'access_request_denied'
      ) {
        return `${ROUTES.ACCESS_STATUS}?tab=denied`;
      }

      return `${ROUTES.ACCESS_STATUS}?tab=pending`;
    }

    if (notification.reference_type === 'document') {
      if (notification.type === 'document_approved') {
        return `${ROUTES.UPLOAD_STATUS}?tab=approved`;
      }
      if (
        notification.type === 'document_rejected' ||
        notification.type === 'document_denied'
      ) {
        return `${ROUTES.UPLOAD_STATUS}?tab=denied`;
      }
      return `${ROUTES.UPLOAD_STATUS}?tab=pending`;
    }

    return ROUTES.NOTIFICATIONS;
  };

  const handleClick = () => {
    if (!notification.is_read) {
      onRead(notification.notification_id);
    }
    navigate(getTargetRoute());
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-4)',
        padding: 'var(--spacing-4) var(--spacing-5)',
        borderBottom: '1px solid var(--color-gray-100)',
        borderLeft: `3px solid ${
          notification.is_read ? 'transparent' : 'var(--color-primary)'
        }`,
        background: notification.is_read
          ? 'white'
          : 'var(--color-primary-50)',
        cursor: 'pointer',
        transition: 'background var(--transition-fast)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = notification.is_read
          ? 'var(--color-gray-50)'
          : 'var(--color-primary-100)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = notification.is_read
          ? 'white'
          : 'var(--color-primary-50)';
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-md)',
          flexShrink: 0,
          background: iconConfig.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconConfig.color,
        }}
      >
        {iconConfig.icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: notification.is_read
              ? 'var(--font-weight-regular)'
              : 'var(--font-weight-semibold)',
            color: 'var(--color-gray-800)',
            marginBottom: '2px',
            lineHeight: 'var(--line-height-tight)',
          }}
        >
          {notification.message}
        </p>
        {fileName && (
          <p
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-primary)',
              fontWeight: 'var(--font-weight-medium)',
              marginBottom: '2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            📄 {fileName}
          </p>
        )}
        {notification.cms_number && (
          <p
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-gray-500)',
              fontFamily: 'var(--font-family-mono)',
              marginBottom: '2px',
            }}
          >
            {notification.cms_number}
          </p>
        )}
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-gray-400)',
          }}
        >
          {formatDistanceToNow(new Date(notification.created_at), {
            addSuffix: true,
          })}
        </p>
      </div>

      {!notification.is_read && (
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'var(--color-primary)',
            flexShrink: 0,
            marginTop: 4,
          }}
        />
      )}
    </div>
  );
};

const NotificationSkeleton: React.FC = () => (
  <>
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          gap: 'var(--spacing-4)',
          padding: 'var(--spacing-4) var(--spacing-5)',
          borderBottom: '1px solid var(--color-gray-100)',
          borderLeft: '3px solid transparent',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-gray-100)',
            flexShrink: 0,
          }}
        />
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-2)',
          }}
        >
          <div
            style={{
              height: 14,
              width: '70%',
              background: 'var(--color-gray-100)',
              borderRadius: 4,
            }}
          />
          <div
            style={{
              height: 12,
              width: '45%',
              background: 'var(--color-gray-100)',
              borderRadius: 4,
            }}
          />
          <div
            style={{
              height: 11,
              width: '25%',
              background: 'var(--color-gray-100)',
              borderRadius: 4,
            }}
          />
        </div>
      </div>
    ))}
  </>
);

const EmptyNotifications: React.FC<{ tab: FilterTab }> = ({ tab }) => (
  <div
    style={{
      padding: 'var(--spacing-16)',
      textAlign: 'center',
    }}
  >
    <Bell
      size={48}
      style={{
        color: 'var(--color-gray-300)',
        margin: '0 auto var(--spacing-4)',
      }}
    />
    <p
      style={{
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--color-gray-600)',
        marginBottom: 'var(--spacing-1)',
      }}
    >
      {tab === 'Unread'
        ? 'All caught up!'
        : tab === 'Read'
        ? 'No read notifications'
        : 'No notifications yet'}
    </p>
    <p
      style={{
        fontSize: 'var(--font-size-sm)',
        color: 'var(--color-gray-400)',
      }}
    >
      {tab === 'Unread'
        ? 'You have no unread notifications at the moment.'
        : tab === 'Read'
        ? 'Notifications you have read will appear here.'
        : 'Notifications about your documents and access requests will appear here.'}
    </p>
  </div>
);

const PAGE_SIZE = 20;

const NotificationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<FilterTab>('Unread');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const { unreadCount, setUnreadCount, decrementCount } =
    useNotificationStore();

  useEffect(() => {
    document.title = 'RRMS | Notifications';
    return () => {
      document.title = 'RRMS';
    };
  }, []);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['notifications', activeTab, page],
    queryFn: () =>
      notificationService.getNotifications({
        page,
        limit: PAGE_SIZE,
        ...(TAB_IS_READ[activeTab] !== undefined && {
          is_read: TAB_IS_READ[activeTab],
        }),
      }),
    select: (r) => {
      const d = r.data?.data ?? r.data;
      const items = (d?.items ?? []).map(mapRowToNotification);
      return {
        items,
        pagination: d?.pagination,
      };
    },
  });

  const notifications = data?.items ?? [];
  const pagination = data?.pagination;

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.markRead(notificationId),
    onSuccess: (_, notificationId) => {
      decrementCount();
      queryClient.setQueryData(
        ['notifications', activeTab, page],
        (old: { items: Notification[]; pagination?: unknown } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((n) =>
              n.notification_id === notificationId
                ? { ...n, is_read: true }
                : n
            ),
          };
        }
      );
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => {
      setUnreadCount(0);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('All notifications marked as read');
    },
    onError: (err: any) => {
      toast.error(
        err.response?.data?.message || 'Failed to mark all as read'
      );
    },
  });

  const handleMarkRead = (notificationId: string) => {
    markReadMutation.mutate(notificationId);
  };

  return (
    <div className="">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
        }}
      >
        <PageHeader
          title="Notifications"
          subtitle="Stay updated on document approvals, access requests, and activity"
        />
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-4)',
              border: '1px solid var(--color-primary)',
              borderRadius: 'var(--radius-md)',
              background: 'white',
              color: 'var(--color-primary)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              opacity: markAllReadMutation.isPending ? 0.6 : 1,
              transition: 'all var(--transition-fast)',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-primary-50)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
          >
            <CheckCheck size={16} />
            {markAllReadMutation.isPending
              ? 'Marking...'
              : `Mark All Read (${unreadCount})`}
          </button>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: 'var(--spacing-1)',
          borderBottom: '1px solid var(--color-gray-200)',
        }}
      >
        {(['All', 'Unread', 'Read'] as FilterTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => {
              setActiveTab(tab);
              setPage(1);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-3) var(--spacing-5)',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontWeight:
                activeTab === tab
                  ? 'var(--font-weight-semibold)'
                  : 'var(--font-weight-regular)',
              color:
                activeTab === tab
                  ? 'var(--color-primary)'
                  : 'var(--color-gray-500)',
              borderBottom:
                activeTab === tab
                  ? '2px solid var(--color-primary)'
                  : '2px solid transparent',
              marginBottom: '-1px',
              transition: 'all var(--transition-fast)',
            }}
          >
            {tab}
            {tab === 'Unread' && unreadCount > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 18,
                  height: 18,
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-primary)',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 'var(--font-weight-bold)',
                  padding: '0 4px',
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {isLoading && <NotificationSkeleton />}

        {isError && (
          <div
            style={{
              padding: 'var(--spacing-10)',
              textAlign: 'center',
            }}
          >
            <AlertCircle
              size={36}
              style={{
                color: 'var(--color-error)',
                margin: '0 auto var(--spacing-3)',
              }}
            />
            <p
              style={{
                color: 'var(--color-error)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              Failed to load notifications
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!isLoading && !isError && notifications.length === 0 && (
          <EmptyNotifications tab={activeTab} />
        )}

        {!isLoading && !isError && notifications.length > 0 && (
          <>
            {notifications.map((notification: Notification) => (
              <NotificationItem
                key={notification.notification_id}
                notification={notification}
                onRead={handleMarkRead}
              />
            ))}

            {pagination && pagination.totalPages > 1 && (
              <div
                style={{
                  padding: 'var(--spacing-4) var(--spacing-5)',
                  borderTop: '1px solid var(--color-gray-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 'var(--spacing-3)',
                }}
              >
                <p
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-gray-500)',
                  }}
                >
                  Showing {(page - 1) * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE, pagination.total)} of{' '}
                  {pagination.total}
                </p>
                <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!pagination.hasPrevPage}
                    style={{
                      padding: 'var(--spacing-1) var(--spacing-3)',
                      border: '1px solid var(--color-gray-200)',
                      borderRadius: 'var(--radius-md)',
                      background: 'white',
                      cursor: pagination.hasPrevPage
                        ? 'pointer'
                        : 'not-allowed',
                      opacity: pagination.hasPrevPage ? 1 : 0.4,
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    Previous
                  </button>
                  <span
                    style={{
                      padding: 'var(--spacing-1) var(--spacing-3)',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-gray-600)',
                    }}
                  >
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setPage((p) =>
                        Math.min(pagination.totalPages, p + 1)
                      )
                    }
                    disabled={!pagination.hasNextPage}
                    style={{
                      padding: 'var(--spacing-1) var(--spacing-3)',
                      border: '1px solid var(--color-gray-200)',
                      borderRadius: 'var(--radius-md)',
                      background: 'white',
                      cursor: pagination.hasNextPage
                        ? 'pointer'
                        : 'not-allowed',
                      opacity: pagination.hasNextPage ? 1 : 0.4,
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
